import React, { useState, useEffect, useCallback } from 'react';
import { createLinkToken, exchangePublicToken, loadPlaidScript } from '../../services/plaidService';
import { Building2, Home, TrendingUp } from 'lucide-react';
import './PlaidLink.css';
import InlineAlert from '../common/InlineAlert';
import { getUserFacingError } from '../../lib/userFacingErrors';

/**
 * PlaidLink component for connecting bank accounts
 */
const ACCOUNT_TYPE_OPTIONS = [
  {
    type: 'bank_credit',
    label: 'Bank / credit card',
    hint: 'Checking, savings, credit cards',
    Icon: Building2,
  },
  {
    type: 'investment',
    label: 'Investment',
    hint: 'Brokerage, retirement, HSA',
    Icon: TrendingUp,
  },
  {
    type: 'loan_mortgage',
    label: 'Loans / mortgages',
    hint: 'Student loans, auto, home',
    Icon: Home,
  },
];

function PlaidLink({
  onSuccess,
  onExit,
  onError,
  onLoadingStateChange,
  onUpgradeRequired,
  onAccountTypeSelected,
  buttonLabel = 'Add account',
  expandedOptions = false,
}) {
  const [loading, setLoading] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState(null);
  const [accountType, setAccountType] = useState('bank_credit');
  const [menuOpen, setMenuOpen] = useState(false);
  const [handlerReady, setHandlerReady] = useState(false);
  const reportError = useCallback((err) => {
    const friendlyError = getUserFacingError(err, 'plaid-link');
    setError(friendlyError);
    onError?.(friendlyError);
  }, [onError]);

  // Load Plaid Link script
  useEffect(() => {
    let isMounted = true;
    loadPlaidScript()
      .then(() => {
        // Plaid script is ready; openPlaidLink also retries this before creating a handler.
      })
      .catch((err) => {
        if (isMounted) reportError(err);
      });

    return () => {
      isMounted = false;
    };
  }, [reportError]);

  // Close account-type dropdown when clicking outside the PlaidLink actions
  useEffect(() => {
    if (!menuOpen) return;

    const handleClickOutside = (event) => {
      const withinActions = event.target.closest('.plaid-link-actions');
      if (!withinActions) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const handleOnSuccess = useCallback(async (publicToken, metadata) => {
    try {
      setLoading(true);
      setHandlerReady(false);
      
      // Exchange public token for access token
      const result = await exchangePublicToken(publicToken);
      
      // Trigger sync in background - DISABLED: Now handled by webhooks
      // syncPlaidData(result.item_id).catch(err => {
      //   console.error('Background sync failed:', err);
      // });
      
      if (onSuccess) {
        onSuccess(result, metadata);
      }
    } catch (err) {
      console.error('Error exchanging token:', err);
      if (err.status === 402 && err.detail?.code === 'free_trial_plaid_limit_reached') {
        onUpgradeRequired?.(err.detail.message);
        return;
      }
      reportError(err);
    } finally {
      setLoading(false);
    }
  }, [onSuccess, onUpgradeRequired, reportError]);

  const handleOnExit = useCallback((err, metadata) => {
    if (err) {
      console.error('Plaid Link error:', err);
      reportError(err);
    }

    setHandlerReady(false);
    
    if (onExit) {
      onExit(err, metadata);
    }
  }, [onExit, reportError]);

  const setPreparingState = useCallback((value) => {
    setPreparing(value);
    if (onLoadingStateChange) onLoadingStateChange(value);
  }, [onLoadingStateChange]);

  const openPlaidLink = useCallback(async (typeOverride) => {
    if (preparing || loading) return;

    try {
      setLoading(true);
      setPreparingState(true);
      setError(null);
      onError?.(null);
      setHandlerReady(false);

      const typeToUse = typeOverride || accountType || 'bank_credit';
      await loadPlaidScript();

      const data = await createLinkToken(typeToUse);
      const token = data.link_token;

      const handler = window.Plaid.create({
        token,
        onSuccess: handleOnSuccess,
        onExit: handleOnExit,
      });
      setHandlerReady(true);
      handler.open();
    } catch (err) {
      console.error('Error creating link token:', err);
      if (err.status === 402 && err.detail?.code === 'free_trial_plaid_limit_reached') {
        onUpgradeRequired?.(err.detail.message);
        return;
      }
      reportError(err);
    } finally {
      setLoading(false);
      setPreparingState(false);
    }
  }, [accountType, handleOnExit, handleOnSuccess, loading, onError, onUpgradeRequired, preparing, reportError, setPreparingState]);

  const handleSelectAccountType = async (type) => {
    setAccountType(type);
    setMenuOpen(false);
    await openPlaidLink(type);
  };

  if (expandedOptions) {
    return (
      <div className="plaid-link-container plaid-link-container-expanded">
        {error && !onError && (
          <InlineAlert
            compact
            title={error.title}
            message={error.message}
          />
        )}
        <div className="plaid-link-options-grid">
          {ACCOUNT_TYPE_OPTIONS.map((option) => (
            <button
              key={option.type}
              type="button"
              className="plaid-link-option-card"
              onClick={() => {
                onAccountTypeSelected?.(option.type);
                handleSelectAccountType(option.type);
              }}
              disabled={loading || preparing}
            >
              <span className="plaid-link-option-icon" aria-hidden="true">
                <option.Icon size={22} />
              </span>
              <span className="plaid-link-option-copy">
                <span className="plaid-link-option-label">{option.label}</span>
                <span className="plaid-link-option-hint">{option.hint}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="plaid-link-container">
      {error && !onError && (
        <InlineAlert
          compact
          title={error.title}
          message={error.message}
        />
      )}
      <div className="plaid-link-actions">
        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          disabled={loading || preparing}
          className="plaid-link-button"
        >
          {loading ? (
            <>
              <span className="spinner-small"></span>
              {handlerReady ? 'Connecting...' : 'Preparing...'}
            </>
          ) : (
            <>
              <span className="icon">＋</span>
              {buttonLabel}
            </>
          )}
        </button>
        {menuOpen && (
          <div className="plaid-link-dropdown">
            {ACCOUNT_TYPE_OPTIONS.map((option) => (
              <button
                key={option.type}
                type="button"
                className="plaid-link-option"
                onClick={() => {
                  onAccountTypeSelected?.(option.type);
                  handleSelectAccountType(option.type);
                }}
              >
                <span className="plaid-link-option-label">{option.label}</span>
                <span className="plaid-link-option-hint">{option.hint}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      </div>
  );
}

export default PlaidLink;
